import { useState, useEffect, useCallback, useMemo} from 'react';
import { StateManager } from '../service/StateManager';
import { EventosService } from '../service/EventosService';

export const useManifiestoState = () => {
    const [operacionData, setOperacionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
    const [refetchTrigger, setRefetchTrigger] = useState(0);

    // Cachear datos en localStorage
    const cachearDatos = useCallback((datos) => {
        const manifiesto = localStorage.getItem('manifiesto');
        if (manifiesto && datos) {
            const datosCache = {
                datos,
                timestamp: new Date().toISOString(),
                manifiesto
            };
            localStorage.setItem(`operacion_${manifiesto}`, JSON.stringify(datosCache));
        }
    }, []);

    // Invalidar cache manualmente
    const invalidarCache = useCallback(() => {
        const manifiesto = localStorage.getItem('manifiesto');
        if (manifiesto) {
            localStorage.removeItem(`operacion_${manifiesto}`);
        }
    }, []);

    // Cargar datos desde cache
    const cargarDesdeCache = useCallback(() => {
        const manifiesto = localStorage.getItem('manifiesto');
        if (!manifiesto) return null;

        try {
            const cached = localStorage.getItem(`operacion_${manifiesto}`);
            if (cached) {
                const datosCache = JSON.parse(cached);
                const tiempoCache = new Date(datosCache.timestamp);
                const ahora = new Date();
                const diferenciaMinutos = (ahora - tiempoCache) / (1000 * 60);

                // Cache válido por 5 minutos
                if (diferenciaMinutos < 5) {
                    return datosCache.datos;
                }
            }
        } catch (error) {
            console.error('Error cargando cache:', error);
        }
        return null;
    }, []);

    // Revalidar el manifiesto y obtener datos actualizados
    const revalidarManifiesto = useCallback(async (forzarRecarga = false) => {
        setLoading(true);
        setError(null);

        try {
            console.log('Iniciando revalidación del manifiesto...', {
                forzarRecarga,
                trigger: refetchTrigger
            });
            // Si se fuerza recarga, invalidar cache primero
            if (forzarRecarga) {
                invalidarCache();
            } else {
                // Intentar cargar desde cache primero
                const datosCache = cargarDesdeCache();
                if (datosCache) {
                    setOperacionData(datosCache);
                    
                    setLoading(false);
                    setUltimaActualizacion(new Date());
                    return datosCache;
                }
            }

            // Cargar desde API
            const result = await EventosService.revalidarManifiesto();
            if (result.success) {
                const operacion = result.operacion || result.data?.operacion || null;
                setOperacionData(operacion);
                // Cachear los nuevos datos
                cachearDatos(operacion);
                setUltimaActualizacion(new Date());
                return operacion;
            } else {
                throw new Error(result.message || 'Error al revalidar el manifiesto');
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [cargarDesdeCache, cachearDatos, invalidarCache, refetchTrigger]);

    // Método específico para forzar re-fetch después de evento
    const refetchDespuesDeEvento = useCallback(async () => {
        // Incrementar trigger para forzar actualización
        setRefetchTrigger(prev => prev + 1);
        // Invalidar cache y recargar desde API
        invalidarCache();
        return await revalidarManifiesto(true);
    }, [revalidarManifiesto, invalidarCache]);

    // Cargar datos iniciales
    useEffect(() => {
        revalidarManifiesto();
    }, [revalidarManifiesto]);

    // Calcular estado operativo basado en los datos del backend
    const estadoOperativo = operacionData ?
        StateManager.determinarEstadoOperativo(operacionData) :
        StateManager.ESTADOS_OPERATIVOS.CARGUE;

    // Obtener estados de eventos para cada fase
    const estadosEventosCargue = operacionData ?
        StateManager.obtenerEstadosEventos(StateManager.FASES.CARGUE, operacionData) :
        {};

    const estadosEventosDescargue = operacionData ?
        StateManager.obtenerEstadosEventos(StateManager.FASES.DESCARGUE, operacionData) :
        {};

    // Información adicional del estado
    const informacionEstado = operacionData ?
        StateManager.obtenerInformacionEstado(operacionData) :
        null;

    // Verificar grupos completados Y pendientes en CARGUE
    const tieneGruposCompletosYPendientesEnCargue = useMemo(() => {
        if (!operacionData) return false;
        return StateManager.tieneGruposCompletosYPendientesEnCargue(operacionData);
    }, [operacionData]);

    // Verificar grupos pendientes de LLEGADA DESCARGUE
    const tieneGruposPendientesLlegadaDescargue = useMemo(() => {
        if (!operacionData) return false;
        return StateManager.tieneGruposPendientesLlegadaDescargue(operacionData);
    }, [operacionData]);

    // Datos para Timeline Modal
    const timelineData = operacionData ? {
        eventosAgrupados: StateManager.agruparEventosPorGrupoRemesas(operacionData),
        kpis: StateManager.calcularKPIsGlobales(operacionData),
        progreso: StateManager.calcularProgresoGlobalManifiesto(operacionData)
    } : null;

    return {
        operacionData,
        loading,
        error,
        estadoOperativo,
        estadosEventosCargue,
        estadosEventosDescargue,
        informacionEstado,
        ultimaActualizacion,
        timelineData,
        revalidarManifiesto,
        refetchDespuesDeEvento,
        tieneGruposCompletosYPendientesEnCargue,
        tieneGruposPendientesLlegadaDescargue
    };
}